const DEFAULT_TERMS_URL = 'https://everyset.com/terms-of-service/';
const DEFAULT_PRIVACY_URL = 'https://everyset.com/privacy-policy/';

let cachedIpAddress: string | null = null;

/**
 * Best-effort fetch of the user's public IP address. Cached after the first
 * successful call. Returns null when the request fails (ad blocker, network
 * error, SSR) or stalls past the timeout so the signup flow is never blocked.
 */
const fetchIpAddress = async (): Promise<string | null> => {
  if (cachedIpAddress) return cachedIpAddress;
  if (typeof window === 'undefined') return null;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    const response = await fetch('https://api.ipify.org?format=json', {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    const data = await response.json();
    cachedIpAddress = data.ip;
    return cachedIpAddress;
  } catch {
    return null;
  }
};

type BuildTermsPrivacyConsentMetadataOptions = {
  /** Terms of Service URL the user actually accepted. */
  termsUrl?: string;
  /** Privacy Policy URL the user actually accepted. */
  privacyUrl?: string;
};

/**
 * Builds the Cognito `clientMetadata` object carrying the Terms & Privacy
 * opt-in consent. Passed to `Auth.signUp` and `Auth.confirmSignUp` so the
 * `casting-auth` Lambda can persist the consent durably in the database.
 *
 * `sourcePage` records which screen the user accepted on (e.g. 'signup').
 * `options.termsUrl` and `options.privacyUrl` should be the country/tenant
 * specific URLs presented to the user (from `clientEnvironment`). They fall
 * back to the default Everyset URLs when not provided.
 */
export const buildTermsPrivacyConsentMetadata = async (
  sourcePage: string,
  options: BuildTermsPrivacyConsentMetadataOptions = {}
): Promise<Record<string, string>> => {
  const ipAddress = await fetchIpAddress();

  return {
    terms_accepted: 'true',
    source_page: sourcePage,
    terms_url: options.termsUrl || DEFAULT_TERMS_URL,
    privacy_url: options.privacyUrl || DEFAULT_PRIVACY_URL,
    ip_address: ipAddress ?? '',
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
};
